import React, { useState } from 'react';
import { AlertTriangle, X, Flag } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const ReportModal = ({ isOpen, onClose, reportedUserId, reportedUserName, reportType = "user", contextId = null }) => {
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const categories = [
    { value: 'spam', label: 'Spam or Advertising', icon: '📧', description: 'Unwanted promotional content' },
    { value: 'phishing', label: 'Phishing or Scam', icon: '🎣', description: 'Attempting to steal information or money' },
    { value: 'harassment', label: 'Harassment or Bullying', icon: '😠', description: 'Abusive or threatening behavior' },
    { value: 'inappropriate_content', label: 'Inappropriate Content', icon: '⚠️', description: 'Sexual, violent, or offensive content' },
    { value: 'fake_profile', label: 'Fake Profile', icon: '🎭', description: 'Pretending to be someone else' },
    { value: 'hate_speech', label: 'Hate Speech', icon: '💢', description: 'Discriminatory or hateful language' },
    { value: 'other', label: 'Other', icon: '📝', description: 'Something else that violates our policies' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!category) {
      toast.error('Please select a category');
      return;
    }

    setSubmitting(true);

    try {
      await axios.post('/users/report', {
        reported_user_id: reportedUserId,
        report_type: reportType,
        category,
        description: description.trim(),
        context_id: contextId
      });

      toast.success('Report submitted successfully. Our team will review it shortly.');
      onClose();
      
      // Reset form
      setCategory('');
      setDescription('');
    } catch (error) {
      console.error('Error submitting report:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit report. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-2xl">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
              <Flag className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-gray-900">Report User</h3>
              <p className="text-sm text-gray-500">Reporting: {reportedUserName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* Info Banner */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start space-x-3">
            <AlertTriangle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-800">
              <p className="font-medium mb-1">Your report is confidential</p>
              <p>We review all reports carefully. False reports may result in account restrictions.</p>
            </div>
          </div>

          {/* Category Selection */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-3">
              Why are you reporting this user? *
            </label>
            <div className="space-y-2">
              {categories.map((cat) => (
                <button
                  key={cat.value}
                  type="button"
                  onClick={() => setCategory(cat.value)}
                  className={`w-full text-left p-4 border-2 rounded-xl transition-all ${
                    category === cat.value
                      ? 'border-red-500 bg-red-50'
                      : 'border-gray-200 hover:border-red-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <span className="text-2xl">{cat.icon}</span>
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{cat.label}</div>
                      <div className="text-sm text-gray-600 mt-0.5">{cat.description}</div>
                    </div>
                    {category === cat.value && (
                      <div className="w-5 h-5 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Additional Details */}
          <div className="mb-6">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Additional Details (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide any additional context that might help us review this report..."
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
              rows="4"
              maxLength="500"
            />
            <p className="text-xs text-gray-500 mt-1">
              {description.length}/500 characters
            </p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-50 transition-colors font-medium"
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!category || submitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl hover:from-red-600 hover:to-red-700 transition-all font-medium shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ReportModal;
